import { useEffect, useState } from "react";

// Core admins (hardcoded) — запасной вход на случай, когда динамический список
// недоступен: useIsAdmin пускает их, не дожидаясь ответа сервера.
//
// Здесь ровно те же пять адресов, что в серверном SUPER_ADMIN_EMAILS
// (routers_fastapi/admin_emails.py): их нельзя удалить и через панель, так что
// два списка не разъезжаются. Всех остальных заводят через админ-панель, она
// пишет config/admin_emails.json в R2 — см. useIsAdmin().
export const ADMIN_EMAILS: string[] = [
    "dmitriyt21@gmail.com",
    "audaryadham@gmail.com",
    "lex.novikoff@gmail.com",
    "petrakov.14ca114@gmail.com",
    "torsunovmanager@gmail.com",
];

// Membership check against the hardcoded core admins only. UX-only — to
// gate admin pages use useIsAdmin(), which also honours dynamically-added
// admins; the bare list here would wrongly deny a newly-added admin.
export const isAdmin = (email: string | null | undefined): boolean => {
    if (!email) return false;
    return ADMIN_EMAILS.includes(email.toLowerCase());
};

// ── Dynamic admins (config/admin_emails.json, managed via Admin Dashboard) ──
// The list holds emails AND vk_<id> identifiers — the same shape as
// User.email — so membership works for both email- and VK-authenticated
// admins. Fetched once per session and cached at module scope.
const ADMIN_EMAILS_URL = "https://site-server.dev.torsunov.pro/api/admin-emails";

let dynamicAdminsCache: string[] | null = null;
let dynamicAdminsInflight: Promise<string[]> | null = null;

const fetchDynamicAdminsOnce = async (): Promise<string[]> => {
    const r = await fetch(ADMIN_EMAILS_URL);
    if (!r.ok) throw new Error(`HTTP ${r.status}`);
    const d = await r.json();
    const list: unknown[] = Array.isArray(d?.data) ? d.data : [];
    return list.map((x) => String(x).toLowerCase());
};

const fetchDynamicAdmins = (): Promise<string[]> => {
    if (dynamicAdminsCache) return Promise.resolve(dynamicAdminsCache);
    if (dynamicAdminsInflight) return dynamicAdminsInflight;

    // Retry transient network failures (mobile network flakiness, brief
    // CORS preflight blips, etc.) with exponential backoff. Without this
    // a single `TypeError: Load failed` on the first try locks the user
    // out of admin pages with a misleading "Not an admin" message even
    // when the next fetch milliseconds later would have succeeded.
    const MAX_ATTEMPTS = 4;
    dynamicAdminsInflight = (async () => {
        let lastErr: unknown;
        for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
            try {
                const list = await fetchDynamicAdminsOnce();
                dynamicAdminsCache = list;
                return list;
            } catch (err) {
                lastErr = err;
                if (attempt < MAX_ATTEMPTS - 1) {
                    await new Promise((res) =>
                        setTimeout(res, 300 * Math.pow(2, attempt)),
                    );
                }
            }
        }
        // All retries failed — clear the inflight so a future mount can
        // try again, but DON'T cache [] (which would permanently lock
        // legitimate admins out until a hard reload).
        dynamicAdminsInflight = null;
        console.warn("admin-emails fetch failed after retries:", lastErr);
        return [] as string[];
    })();
    return dynamicAdminsInflight;
};

/** Non-hook form of useIsAdmin for code outside React (the activity log header). */
export const resolveIsAdmin = async (identifier: string): Promise<boolean> => {
    const id = identifier.toLowerCase();
    if (!id) return false;
    return ADMIN_EMAILS.includes(id) || (await fetchDynamicAdmins()).includes(id);
};

/**
 * Admin gate that honours BOTH the hardcoded core admins and the
 * dynamically-managed list (config/admin_emails.json). Use this — not the
 * bare isAdmin() — to gate admin pages, otherwise a newly-added admin is
 * wrongly denied.
 *
 * `identifier` is User.email: a real email for email/Google auth, or
 * `vk_<id>` for VK auth. While the dynamic list is loading it returns
 * { isAdmin: false, loading: true } so callers can show a checking state
 * instead of a premature "access denied".
 *
 * In dev mode (REACT_APP_DEV_MODE) the UserProvider signs everyone in as
 * DEV_ADMIN_USER, whose vk_<id> is in neither list — so admin pages would
 * bounce on localhost. Treat dev mode as admin, matching that intent.
 */
export const useIsAdmin = (
    identifier: string | null | undefined,
): { isAdmin: boolean; loading: boolean } => {
    const isDev = process.env.REACT_APP_DEV_MODE === "true";
    const id = (identifier || "").toLowerCase();
    const isCoreAdmin = isDev || (!!id && ADMIN_EMAILS.includes(id));
    const [dynamic, setDynamic] = useState<string[] | null>(dynamicAdminsCache);

    useEffect(() => {
        if (dynamicAdminsCache) {
            setDynamic(dynamicAdminsCache);
            return;
        }
        let alive = true;
        fetchDynamicAdmins().then((list) => {
            if (alive) setDynamic(list);
        });
        return () => {
            alive = false;
        };
    }, []);

    if (isCoreAdmin) return { isAdmin: true, loading: false };
    if (dynamic === null) return { isAdmin: false, loading: true };
    return { isAdmin: !!id && dynamic.includes(id), loading: false };
};

// ── "View as a regular user" preview ────────────────────────────────────────
//
// Admins see extra things on the public surfaces (precision badges, unreleased
// features, review aids). There was no way to check what a normal visitor
// actually sees without logging out or opening a private window, which is
// exactly when a leak ships unnoticed.
//
// This flag flips CONTENT gating only. It deliberately does NOT feed
// useIsAdmin(), because that also guards access to the admin pages themselves —
// previewing must not lock an admin out of the dashboard they are previewing
// from. Gate admin-only UI on public pages with useAdminUI() instead.
export const ADMIN_PREVIEW_KEY = "admin_view_as_user";

const previewListeners = new Set<(v: boolean) => void>();

export const getAdminPreviewAsUser = (): boolean => {
    try {
        return localStorage.getItem(ADMIN_PREVIEW_KEY) === "1";
    } catch {
        return false;
    }
};

export const setAdminPreviewAsUser = (on: boolean): void => {
    try {
        if (on) localStorage.setItem(ADMIN_PREVIEW_KEY, "1");
        else localStorage.removeItem(ADMIN_PREVIEW_KEY);
    } catch {
        /* private mode — the toggle just won't persist */
    }
    previewListeners.forEach((fn) => fn(on));
};

/** Subscribe to preview toggles outside React; returns the unsubscribe. */
export const subscribeAdminPreview = (fn: (on: boolean) => void): (() => void) => {
    previewListeners.add(fn);
    return () => {
        previewListeners.delete(fn);
    };
};

/**
 * Admin state for CONTENT on public pages.
 *
 * `isAdmin` is false while previewing, so every admin-only element disappears
 * exactly as it would for a visitor. `isRealAdmin` stays true so the toggle
 * itself (and any "you are previewing" affordance) can still be rendered.
 */
export const useAdminUI = (
    identifier: string | null | undefined,
): {
    isAdmin: boolean;
    isRealAdmin: boolean;
    loading: boolean;
    previewAsUser: boolean;
    setPreviewAsUser: (on: boolean) => void;
} => {
    const { isAdmin: real, loading } = useIsAdmin(identifier);
    const [preview, setPreview] = useState<boolean>(getAdminPreviewAsUser());

    useEffect(() => {
        const fn = (v: boolean) => setPreview(v);
        previewListeners.add(fn);
        return () => {
            previewListeners.delete(fn);
        };
    }, []);

    return {
        isAdmin: real && !preview,
        isRealAdmin: real,
        loading,
        previewAsUser: preview,
        setPreviewAsUser: setAdminPreviewAsUser,
    };
};
